import { api } from "@/utils/Axios";

/**
 * Slug helpers
 * - safer: trims, lowercases (optional), removes duplicate hyphens
 * - stable: avoids trailing/leading hyphens
 */
export function convertSpacesToHyphens(title) {
  if (!title || typeof title !== "string") return "";

  return title
    .trim()
    .replace(/\//g, "-")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

/**
 * Distance in km (Haversine)
 * - validates numbers to avoid NaN propagation
 */
export const getDistanceInKm = (lat1, lon1, lat2, lon2) => {
  const n1 = Number(lat1),
    n2 = Number(lon1),
    n3 = Number(lat2),
    n4 = Number(lon2);

  if (![n1, n2, n3, n4].every(Number.isFinite)) return null;

  const R = 6371;
  const dLat = ((n3 - n1) * Math.PI) / 180;
  const dLon = ((n4 - n2) * Math.PI) / 180;

  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((n1 * Math.PI) / 180) *
      Math.cos((n3 * Math.PI) / 180) *
      Math.sin(dLon / 2) ** 2;

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

/**
 * Get first long paragraph from HTML string
 * - faster & safer: stop early, avoid building a full array
 * - handles <p ...> attributes, &nbsp;, and extra whitespace
 */
export function getFirstLongParagraph(intro, minLen = 50, previewLen = 100) {
  if (!intro || typeof intro !== "string") return null;

  const re = /<p\b[^>]*>(.*?)<\/p>/gis;
  let match;

  while ((match = re.exec(intro)) !== null) {
    const text = match[1]
      .replace(/&nbsp;/gi, " ")
      .replace(/<[^>]*>/g, "") // strip nested tags inside <p>
      .trim()
      .replace(/\s+/g, " ");

    if (text.length >= minLen) {
      return text.length > previewLen
        ? `${text.slice(0, previewLen)}...`
        : text;
    }
  }

  return null;
}

/**
 * Products cache (sessionStorage)
 * - SSR safe
 * - avoids storing "null" string
 * - optional TTL to reduce repeated API hits
 */
const PRODUCTS_KEY = "Products";
const PRODUCTS_TS_KEY = "Products_TS";
const DEFAULT_TTL_MS = 30 * 60 * 1000; // 30 minutes

export const fetchProducts = async (ttlMs = DEFAULT_TTL_MS) => {
  if (typeof window === "undefined") return null;

  try {
    const cached = sessionStorage.getItem(PRODUCTS_KEY);
    const tsRaw = sessionStorage.getItem(PRODUCTS_TS_KEY);

    if (cached && tsRaw) {
      const ts = Number(tsRaw);
      if (Number.isFinite(ts) && Date.now() - ts < ttlMs) {
        return JSON.parse(cached);
      }
    }

    const res = await api.post("/services");
    const data = res?.data ?? null;

    if (data) {
      sessionStorage.setItem(PRODUCTS_KEY, JSON.stringify(data));
      sessionStorage.setItem(PRODUCTS_TS_KEY, String(Date.now()));
    } else {
      sessionStorage.removeItem(PRODUCTS_KEY);
      sessionStorage.removeItem(PRODUCTS_TS_KEY);
    }

    return data;
  } catch {
    sessionStorage.removeItem(PRODUCTS_KEY);
    sessionStorage.removeItem(PRODUCTS_TS_KEY);
    return null;
  }
};

export const formatNumber = (value) => {
  if (typeof value !== "number") return value; // handle unexpected input safely
  return value.toLocaleString("en-US");
};
export const formatYearsLabel = (count, locale) => {
  const num = Number(count);

  if (locale === "en") {
    return `${formatNumber(num)} ${num === 1 ? "year" : "years"}`;
  }

  // Arabic rules
  if (num === 1) return `سنة`;
  if (num === 2) return `سنتان`;
  if (num >= 3 && num <= 10) return `${formatNumber(num)} سنوات`;

  return `${formatNumber(num)} سنة`; // 11+
};
export const getLocalizedValue = (data, key, locale, fallback = "") => {
  if (!data || !key || !locale) return fallback;
  return data[`${key}_${locale}`] || fallback;
};
/* function to transform data  */
export function getStaticTextByKey(data) {
  if (!Array.isArray(data)) return {};

  return data.reduce((acc, item) => {
    acc[item.key] = item;
    return acc;
  }, {});
}
export const toArabicNumber = (num) => {
  return String(num).replace(/\d/g, (digit) => "٠١٢٣٤٥٦٧٨٩"[digit]);
};
